// The launch/convergence machine (sdk ADR-016, LIFECYCLE-STATE-MACHINES.md §1b).

export type ConvergenceStep =
  | 'locate_bundle'
  | 'locate_registrar'
  | 'register'
  | 'await_daemon'
  | 'await_tray';

export const ALL_CONVERGENCE_STEPS: readonly ConvergenceStep[] = [
  'locate_bundle',
  'locate_registrar',
  'register',
  'await_daemon',
  'await_tray',
];

export type LaunchState =
  | { state: 'launched' }
  | { state: 'spawning_children' }
  | { state: 'converging' }
  | { state: 'already_running' }
  | { state: 'stale_runtime' }
  | { state: 'registering' }
  | { state: 'bootstrapping' }
  | { state: 'daemon_ready' }
  | {
      state: 'convergence_failed';
      step: ConvergenceStep;
      cause: string;
      /** A prior run of this realm reached a daemon, so this is not a first registration. */
      hadConverged: boolean;
    };

export type LaunchStateName = LaunchState['state'];

export interface LaunchEntry {
  platform: string;
  packaged: boolean;
  desktopLaunchEnv: string | undefined;
}

/** Convergence is the packaged, non-`la`-spawned darwin path (LIFECYCLE-STATE-MACHINES.md §1b). */
export function convergesOnLaunch(entry: LaunchEntry): boolean {
  return entry.platform === 'darwin' && entry.packaged && entry.desktopLaunchEnv !== '1';
}

export function launchAfterStart(entry: LaunchEntry): LaunchState {
  return convergesOnLaunch(entry) ? { state: 'converging' } : { state: 'spawning_children' };
}

/** `mtime_ns` is a decimal string — it exceeds a double's exact range (shogunchop ADR-007). */
export interface BinaryIdentity {
  size: number;
  mtime_ns: string;
}

export interface ConvergenceLive {
  daemonLive: boolean;
  trayLive: boolean;
  runningIdentity?: BinaryIdentity | null;
  installedIdentity?: BinaryIdentity | null;
}

export function sameBinary(a: BinaryIdentity, b: BinaryIdentity): boolean {
  return a.size === b.size && a.mtime_ns === b.mtime_ns;
}

/** Live is not current: a bundle swapped under a live daemon is `stale_runtime` (sdk ADR-016, shogunchop ADR-007). */
export function convergingAfterDiscovery(live: ConvergenceLive): LaunchState {
  if (!live.daemonLive || !live.trayLive) return { state: 'registering' };
  const installed = live.installedIdentity;
  if (installed == null) return { state: 'already_running' };
  const running = live.runningIdentity;
  if (running == null || !sameBinary(running, installed)) return { state: 'stale_runtime' };
  return { state: 'already_running' };
}

export function convergenceFailed(
  step: ConvergenceStep,
  cause: string,
  hadConverged: boolean,
): LaunchState {
  return { state: 'convergence_failed', step, cause, hadConverged };
}

export function isLaunchTerminal(state: LaunchState): boolean {
  return state.state === 'convergence_failed';
}

/** A launch that wrote nothing and bootstrapped nothing. */
export function isConvergenceInert(trail: readonly LaunchState[]): boolean {
  return !trail.some(
    (s) => s.state === 'registering' || s.state === 'stale_runtime' || s.state === 'bootstrapping',
  );
}

/** The state the launch reached — the one the renderer reports on (sdk ADR-016). */
export function launchOutcome(trail: readonly LaunchState[]): LaunchState {
  const last = trail[trail.length - 1];
  if (last === undefined) {
    throw new Error('launchOutcome: an empty trail describes no launch');
  }
  return last;
}

export type LaunchRefusalCode =
  | 'convergence-bundle-missing'
  | 'convergence-registrar-missing'
  | 'convergence-script-failed'
  | 'convergence-daemon-silent'
  | 'convergence-tray-silent';

const REFUSAL_BY_STEP: Record<ConvergenceStep, LaunchRefusalCode> = {
  locate_bundle: 'convergence-bundle-missing',
  locate_registrar: 'convergence-registrar-missing',
  register: 'convergence-script-failed',
  await_daemon: 'convergence-daemon-silent',
  await_tray: 'convergence-tray-silent',
};

export const ALL_LAUNCH_REFUSAL_CODES: readonly LaunchRefusalCode[] =
  ALL_CONVERGENCE_STEPS.map((step) => REFUSAL_BY_STEP[step]);

/** One named cause per terminal launch failure. */
export function launchRefusalCode(step: ConvergenceStep): LaunchRefusalCode {
  return REFUSAL_BY_STEP[step];
}

/** The refusal a terminal launch presents, or null while the launch can still proceed. */
export function launchRefusal(state: LaunchState): LaunchRefusalCode | null {
  return state.state === 'convergence_failed' ? launchRefusalCode(state.step) : null;
}
